import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { useIsomorphicLayoutEffect } from '@/lib/useIsomorphicLayoutEffect'
import { Head } from 'vite-react-ssg'
import { CheckCircle2 } from 'lucide-react'
import { useLocation } from 'react-router-dom'

import ScrollReveal from '@/components/ScrollReveal'
import { EditorialFaq } from '@/components/EditorialFaq'
import { TrustedClinicsMarquee } from '@/components/TrustedClinics'
import { captureAttribution } from '@/lib/attribution'
import { wistiaVideoSchema } from '@/data/seoConstants'
import { DOCTORS_AGENT_FAQS, DOCTORS_AGENT_FEATURES } from '@/data/doctorsAgentContent'

const LazyLeadCaptureModal = lazy(() =>
    import('@/components/LeadCaptureModal').then((m) => ({ default: m.LeadCaptureModal })),
)
const WISTIA_MEDIA_ID = import.meta.env.VITE_WISTIA_MEDIA_ID ?? 'na1pc11zly'
const WISTIA_PLAYER_JS = 'https://fast.wistia.com/player.js'

function loadWistiaScripts(mediaId: string): Promise<void> {
    const loadScript = (src: string, type?: string) =>
        new Promise<void>((resolve, reject) => {
            if (document.querySelector(`script[src="${src}"]`)) {
                resolve()
                return
            }

            const script = document.createElement('script')
            script.src = src
            script.async = true
            if (type) script.type = type
            script.onload = () => resolve()
            script.onerror = () => reject(new Error(`Failed to load ${src}`))
            document.head.appendChild(script)
        })

    return Promise.all([
        loadScript(WISTIA_PLAYER_JS),
        loadScript(`https://fast.wistia.com/embed/${mediaId}.js`, 'module'),
    ]).then(() => undefined)
}

function buildWistiaFirstFrameUrl(thumbnailUrl: string): string {
    const url = new URL(thumbnailUrl)
    url.searchParams.set('video_still_time', '0')
    // Don't use image_crop_resized, it center-crops and clips edge content (e.g. left-side text)
    return url.toString()
}

async function fetchWistiaFirstFrameUrl(mediaId: string): Promise<string> {
    const oembedUrl = `https://fast.wistia.com/oembed?url=${encodeURIComponent(`https://home.wistia.com/medias/${mediaId}`)}&format=json`
    const response = await fetch(oembedUrl)
    if (!response.ok) throw new Error('Wistia oEmbed request failed')

    const data = (await response.json()) as { thumbnail_url?: string }
    if (!data.thumbnail_url) throw new Error('Wistia oEmbed response missing thumbnail_url')

    return buildWistiaFirstFrameUrl(data.thumbnail_url)
}

const VSL_ACCENT = '#7B00FF'
/** Fixed bar is two lines on narrow viewports; reserve space before measure to avoid CLS. */
const BANNER_HEIGHT_FALLBACK = 52

const FONT = 'Poppins, sans-serif'
const HERO_GRADIENT = 'linear-gradient(135deg, #3b1f6e 0%, #1f223c 48%, #162035 100%)'
const HERO_ACCENT = { fontStyle: 'italic' as const, fontWeight: 400, color: '#c4b5fd' }
/** Navy-to-white fold: fraction of video height from its top (0.82 ≈ red line through lower video) */
const FOLD_VIDEO_RATIO = 0.82

const FAQ_SCHEMA = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: DOCTORS_AGENT_FAQS.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
        },
    })),
}


export const DoctorsAgent = () => {
    const location = useLocation()
    const isDoctorsAgent2Route = location.pathname.startsWith('/doctors-agent-2')
    const canonicalPath = isDoctorsAgent2Route ? '/doctors-agent-2' : '/doctors-agent'
    const thankYouPath = isDoctorsAgent2Route ? '/doctors-agent-2/merci' : '/doctors-agent/merci'
    const unqualifiedThankYouPath = isDoctorsAgent2Route ? '/doctors-agent-2/merci-non-qualifie' : '/doctors-agent/merci-non-qualifie'
    const landingSource = isDoctorsAgent2Route ? 'doctors-agent-2' : 'doctors-agent'
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [shouldLoadModal, setShouldLoadModal] = useState(false)
    const [shouldLoadPlayer, setShouldLoadPlayer] = useState(false)
    const [playerScriptsReady, setPlayerScriptsReady] = useState(false)
    const [playerReady, setPlayerReady] = useState(false)
    // Default to the pre-optimized local poster so LCP never waits on a Wistia
    // oEmbed round-trip; upgrade to the live Wistia thumbnail below if it resolves.
    const [posterUrl, setPosterUrl] = useState('/images/doctors-agent-vsl-poster.webp')
    const isVideoPlaying = shouldLoadPlayer && playerReady
    const playerRef = useRef<HTMLElement | null>(null)
    const videoContainerRef = useRef<HTMLDivElement | null>(null)
    const bannerRef = useRef<HTMLDivElement | null>(null)
    const pageRef = useRef<HTMLDivElement | null>(null)
    const [bannerHeight, setBannerHeight] = useState(BANNER_HEIGHT_FALLBACK)
    const [foldHeight, setFoldHeight] = useState<number | null>(null)

    const openModal = () => {
        setShouldLoadModal(true)
        setIsModalOpen(true)
    }

    const playVideo = () => {
        setShouldLoadPlayer(true)
    }

    useEffect(() => {
        captureAttribution()
    }, [])

    // Pre-load Wistia scripts silently when the browser is idle so they are
    // already cached when the user clicks play, eliminates the script-download delay.
    useEffect(() => {
        let cancelled = false
        const prime = () => {
            if (cancelled) return
            loadWistiaScripts(WISTIA_MEDIA_ID).catch(() => {/* ignore, will retry on click */})
        }

        let cleanupTimer: (() => void) | undefined
        if (typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(prime, { timeout: 4000 })
            cleanupTimer = () => window.cancelIdleCallback(id)
        } else {
            const id = setTimeout(prime, 3500)
            cleanupTimer = () => clearTimeout(id)
        }

        return () => {
            cancelled = true
            cleanupTimer?.()
        }
    }, [])

    // Upgrade to the live Wistia thumbnail only once the browser is idle, well after
    // the local WebP has painted — swapping the <img src> earlier makes the browser
    // register the later Wistia image as a new LCP candidate instead of crediting the
    // poster that actually painted first (see forteunoweb.com-audit/findings/performance.md).
    useEffect(() => {
        let cancelled = false
        const upgrade = () => {
            if (cancelled) return
            fetchWistiaFirstFrameUrl(WISTIA_MEDIA_ID)
                .then((url) => {
                    if (!cancelled) setPosterUrl(url)
                })
                .catch(() => {
                    // Keep the local WebP fallback if oEmbed is unavailable.
                })
        }

        let cleanupTimer: (() => void) | undefined
        if (typeof window.requestIdleCallback === 'function') {
            const id = window.requestIdleCallback(upgrade, { timeout: 4000 })
            cleanupTimer = () => window.cancelIdleCallback(id)
        } else {
            const id = setTimeout(upgrade, 3500)
            cleanupTimer = () => clearTimeout(id)
        }

        return () => {
            cancelled = true
            cleanupTimer?.()
        }
    }, [])

    useIsomorphicLayoutEffect(() => {
        const banner = bannerRef.current
        if (!banner) return

        const updateHeight = () => setBannerHeight(banner.offsetHeight)
        updateHeight()

        const observer = new ResizeObserver(updateHeight)
        observer.observe(banner)
        return () => observer.disconnect()
    }, [])

    useIsomorphicLayoutEffect(() => {
        const updateFold = () => {
            const page = pageRef.current
            const video = videoContainerRef.current
            if (!page || !video) return

            const pageTop = page.getBoundingClientRect().top + window.scrollY
            const videoRect = video.getBoundingClientRect()
            const fold =
                videoRect.top + window.scrollY - pageTop + videoRect.height * FOLD_VIDEO_RATIO
            setFoldHeight(fold)
        }

        updateFold()
        window.addEventListener('resize', updateFold)
        const observer = new ResizeObserver(updateFold)
        if (pageRef.current) observer.observe(pageRef.current)
        if (videoContainerRef.current) observer.observe(videoContainerRef.current)
        if (bannerRef.current) observer.observe(bannerRef.current)

        return () => {
            window.removeEventListener('resize', updateFold)
            observer.disconnect()
        }
    }, [bannerHeight, isVideoPlaying])

    useEffect(() => {
        if (!shouldLoadPlayer) return

        let cancelled = false
        // Scripts may already be cached from the idle prefetch, loadWistiaScripts
        // short-circuits immediately if the <script> tags already exist in the DOM.
        loadWistiaScripts(WISTIA_MEDIA_ID)
            .then(() => {
                if (!cancelled) setPlayerScriptsReady(true)
            })
            .catch(() => {
                if (!cancelled) setPlayerReady(true)
            })

        return () => {
            cancelled = true
        }
    }, [shouldLoadPlayer])

    useEffect(() => {
        if (!playerScriptsReady) return

        let cleanup: (() => void) | undefined

        const attach = () => {
            const player = playerRef.current
            if (!player) {
                requestAnimationFrame(attach)
                return
            }

            const markReady = () => setPlayerReady(true)
            if ('roundedPlayer' in player) {
                (player as HTMLElement & { roundedPlayer: number }).roundedPlayer = 0
            }
            player.addEventListener('play', markReady)
            const fallback = window.setTimeout(markReady, 4000)
            cleanup = () => {
                player.removeEventListener('play', markReady)
                clearTimeout(fallback)
            }
        }

        attach()
        return () => cleanup?.()
    }, [playerScriptsReady])

    return (
        <div
            ref={pageRef}
            style={{
                position: 'relative',
                minHeight: '100vh',
                overflowX: 'hidden',
                backgroundColor: '#ffffff',
                fontFamily: FONT,
                color: '#111111',
            }}
        >
            {foldHeight !== null && (
                <div
                    aria-hidden
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: foldHeight,
                        background: HERO_GRADIENT,
                        zIndex: 0,
                    }}
                />
            )}

            <div style={{ position: 'relative', zIndex: 1 }}>
            <Head>
                <html lang="fr" />
                <link rel="icon" type="image/png" href="/favicon.png" />
                <link rel="apple-touch-icon" href="/favicon.png" />
                <title>Cabinet Pilote : Rendez-vous Automatique Dentistes</title>
                <meta
                    name="description"
                    content="Cabinet Pilote installe un système automatique de rendez-vous pour dentistes en 48h. Essai 7 jours gratuit avec vos vrais patients."
                />
                <meta property="og:image" content="https://forteunoweb.com/og-image.jpg" />
                <link rel="canonical" href={`https://forteunoweb.com${canonicalPath}`} />
                {/* -2 is an ad variant with identical content: keep it out of the index so it can't compete with the original. */}
                {isDoctorsAgent2Route ? <meta name="robots" content="noindex, follow" /> : null}
                <meta property="og:title" content="Cabinet Pilote | Forteuno" />
                <meta
                    property="og:description"
                    content="Système automatique de rendez-vous pour cabinets dentaires, essai 7 jours gratuit."
                />
                <meta property="og:url" content={`https://forteunoweb.com${canonicalPath}`} />
                <meta property="og:type" content="website" />
                <link rel="preload" as="image" href={posterUrl} />
                <link rel="preconnect" href="https://fast.wistia.com" />
                <link rel="preconnect" href="https://embed-ssl.wistia.com" crossOrigin="" />
                <script type="application/ld+json">{JSON.stringify(FAQ_SCHEMA)}</script>
                <script type="application/ld+json">
                    {JSON.stringify(wistiaVideoSchema({
                        pageUrl: `https://forteunoweb.com${canonicalPath}`,
                        mediaId: WISTIA_MEDIA_ID,
                        name: 'Cabinet Pilote : Rendez-vous Automatique Dentistes',
                        description: 'Système automatique de rendez-vous pour cabinets dentaires, essai 7 jours gratuit.',
                        thumbnailUrl: `https://forteunoweb.com${posterUrl}`,
                        uploadDate: '2026-02-13',
                        inLanguage: 'fr-MA',
                    }))}
                </script>
            </Head>

            {/* Top announcement bar, fixed on scroll */}
            <div
                ref={bannerRef}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: '#000000',
                    padding: '10px 12px',
                    textAlign: 'center',
                    fontFamily: FONT,
                }}
            >
                <p
                    style={{
                        color: '#ffffff',
                        fontSize: 'clamp(10px, 2.6vw, 13px)',
                        fontWeight: 600,
                        letterSpacing: '0.01em',
                        lineHeight: 1.45,
                        margin: 0,
                        maxWidth: '520px',
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}
                >
                    <span
                        style={{
                            display: 'inline-block',
                            width: '7px',
                            height: '7px',
                            backgroundColor: '#ef4444',
                            borderRadius: '50%',
                            marginRight: '8px',
                            verticalAlign: 'middle',
                            position: 'relative',
                            top: '-1px',
                        }}
                    />
                    Dentistes · Essai 7 jours · Installation offerte (au lieu de{' '}
                    <span style={{ textDecoration: 'line-through', color: '#f87171' }}>3 000 DH</span>)
                </p>
            </div>

            {/* Hero, heading + video; navy fold is drawn by the absolute bg layer above */}
            <div
                style={{
                    paddingTop: `calc(${bannerHeight}px + clamp(28px, 5vw, 56px))`,
                    paddingLeft: 'clamp(16px, 4vw, 24px)',
                    paddingRight: 'clamp(16px, 4vw, 24px)',
                    textAlign: 'center',
                }}
            >
                <div
                    className="da-hero-inner"
                    style={{
                        margin: '0 auto',
                        width: '100%',
                        maxWidth: 'min(920px, 100%)',
                    }}
                >
                    <h1
                        style={{
                            fontFamily: FONT,
                            fontSize: 'clamp(26px, 4.25vw, 42px)',
                            fontWeight: 800,
                            lineHeight: 1.22,
                            color: '#ffffff',
                            textAlign: 'center',
                            margin: '0 auto',
                            letterSpacing: '-0.01em',
                            width: '100%',
                            maxWidth: '920px',
                        }}
                    >
                        Répondre en <span style={HERO_ACCENT}>30 secondes</span> capte le <span style={HERO_ACCENT}>patient</span>
                        <br />
                        Lui montrer son <span style={HERO_ACCENT}>futur sourire</span> le fait réserver
                    </h1>

                    <p
                        style={{
                            fontFamily: FONT,
                            fontSize: 'clamp(15px, 2.2vw, 18px)',
                            fontWeight: 400,
                            lineHeight: 1.65,
                            color: 'rgba(255,255,255,0.92)',
                            textAlign: 'center',
                            margin: 'clamp(16px, 3vw, 24px) auto clamp(20px, 4vw, 32px)',
                            width: '100%',
                            maxWidth: '760px',
                        }}
                    >
                        Cabinet Pilote fait les deux, <span style={HERO_ACCENT}>automatiquement</span>. C&apos;est ce qui vous{' '}
                        <span style={HERO_ACCENT}>distingue</span> du cabinet d&apos;en face
                    </p>

                    <div ref={videoContainerRef} className="da-vsl-player" style={{ borderRadius: 0 }}>
                        {!isVideoPlaying ? (
                            <button
                                type="button"
                                className="da-vsl-header"
                                onClick={playVideo}
                                style={{ fontFamily: FONT }}
                            >
                                Cliquez sur le bouton lecture pour regarder la vidéo
                            </button>
                        ) : (
                            <div className="da-vsl-header" style={{ fontFamily: FONT }}>
                                Cliquez sur le bouton lecture pour regarder la vidéo
                            </div>
                        )}

                        <div className="da-vsl-screen">
                            {!isVideoPlaying && (
                                <>
                                    <img
                                        src={posterUrl}
                                        alt="Aperçu de la vidéo Cabinet Pilote"
                                        width={1280}
                                        height={714}
                                        sizes="(max-width: 768px) 92vw, 640px"
                                        className="da-vsl-poster"
                                        fetchpriority="high"
                                        decoding="async"
                                    />
                                    <div className="da-vsl-poster-shade" aria-hidden />

                                    <button
                                        type="button"
                                        className="da-vsl-play-btn"
                                        onClick={playVideo}
                                        aria-label="Lire la vidéo"
                                        style={{ backgroundColor: VSL_ACCENT }}
                                    >
                                        <svg className="da-vsl-play-icon" viewBox="0 0 32 32" fill="none" aria-hidden>
                                            <path d="M11 8v16l15-8-15-8z" fill="#ffffff" />
                                        </svg>
                                    </button>

                                    {shouldLoadPlayer && !playerReady && (
                                        <div className="da-vsl-loading" aria-live="polite" aria-busy="true">
                                            <span className="da-vsl-loading-spinner" style={{ borderTopColor: VSL_ACCENT }} />
                                        </div>
                                    )}
                                </>
                            )}

                            {shouldLoadPlayer && playerScriptsReady && (
                                <wistia-player
                                    ref={playerRef}
                                    media-id={WISTIA_MEDIA_ID}
                                    aspect="1.7777777777777777"
                                    autoplay="true"
                                    big-play-button="false"
                                    fit-strategy="contain"
                                    rounded-player="0"
                                    player-color="#7B00FF"
                                    className="da-wistia-player"
                                    style={{
                                        opacity: isVideoPlaying ? 1 : 0,
                                        pointerEvents: isVideoPlaying ? 'auto' : 'none',
                                    }}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <TrustedClinicsMarquee locale="fr" />

            {/* Main content, white section below video fold */}
            <main style={{ maxWidth: '700px', margin: '0 auto', padding: 'clamp(28px, 5vw, 40px) clamp(16px, 4vw, 24px) 80px' }}>

                {/* First CTA */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '28px', gap: '10px' }}>
                    <button
                        type="button"
                        onClick={openModal}
                        style={{
                            fontFamily: FONT,
                            width: '100%',
                            maxWidth: '560px',
                            padding: 'clamp(16px, 3vw, 22px) clamp(20px, 4vw, 40px)',
                            backgroundColor: '#7B00FF',
                            color: '#ffffff',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: 'clamp(14px, 2.6vw, 18px)',
                            fontWeight: 700,
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            boxShadow: '0 6px 24px rgba(123,0,255,0.35)',
                            transition: 'filter 0.2s, box-shadow 0.2s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.filter = 'brightness(1.12)'
                            e.currentTarget.style.boxShadow = '0 10px 32px rgba(123,0,255,0.5)'
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.filter = 'brightness(1)'
                            e.currentTarget.style.boxShadow = '0 6px 24px rgba(123,0,255,0.35)'
                        }}
                    >
                        RÉSERVER MON INSTALLATION GRATUITE
                    </button>
                    <span style={{ fontFamily: FONT, fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>
                        Agent actif en 48h · Sans engagement
                    </span>
                </div>

                {/* What you get */}
                <ScrollReveal>
                    <h2
                        style={{
                            fontFamily: FONT,
                            fontSize: 'clamp(28px, 5.2vw, 48px)',
                            fontWeight: 800,
                            color: '#111111',
                            textAlign: 'center',
                            marginTop: 'clamp(40px, 6vw, 64px)',
                            marginBottom: 'clamp(28px, 5vw, 44px)',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2,
                            maxWidth: '680px',
                            marginLeft: 'auto',
                            marginRight: 'auto',
                            minHeight: 'clamp(34px, 6.24vw, 58px)',
                        }}
                    >
                        OK... Je reçois quoi exactement&nbsp;?
                    </h2>

                    <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 40px 0', display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '680px', marginLeft: 'auto', marginRight: 'auto' }}>
                        {DOCTORS_AGENT_FEATURES.map((feature) => (
                            <li key={feature.title} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                                <CheckCircle2 style={{ width: '20px', height: '20px', color: '#7B00FF', flexShrink: 0, marginTop: '3px' }} />
                                <p style={{ fontFamily: FONT, fontSize: '16px', color: '#374151', lineHeight: 1.65, margin: 0 }}>
                                    <strong style={{ color: '#111111', fontWeight: 700 }}>{feature.title}</strong>
                                    <br />
                                    {feature.body}
                                </p>
                            </li>
                        ))}
                    </ul>

                    <section aria-labelledby="doctors-agent-faq-title" className="mb-12 mt-14">
                        <EditorialFaq
                            heading="Ce que les cabinets veulent savoir avant de commencer"
                            items={DOCTORS_AGENT_FAQS}
                            headingId="doctors-agent-faq-title"
                        />
                    </section>


                    <p
                        style={{
                            fontFamily: FONT,
                            fontSize: '16px',
                            fontWeight: 600,
                            color: '#6b7280',
                            textAlign: 'center',
                            marginBottom: '20px',
                            letterSpacing: '0.01em',
                        }}
                    >
                        Essai 7 jours · Cabinet Pilote
                    </p>

                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <button
                            type="button"
                            onClick={openModal}
                            style={{
                                fontFamily: FONT,
                                width: '100%',
                                maxWidth: '560px',
                                padding: 'clamp(16px, 3vw, 22px) clamp(20px, 4vw, 40px)',
                                backgroundColor: '#7B00FF',
                                color: '#ffffff',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: 'clamp(14px, 2.6vw, 18px)',
                                fontWeight: 700,
                                letterSpacing: '0.04em',
                                textTransform: 'uppercase',
                                boxShadow: '0 6px 24px rgba(123,0,255,0.35)',
                                transition: 'filter 0.2s, box-shadow 0.2s',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.filter = 'brightness(1.12)'
                                e.currentTarget.style.boxShadow = '0 10px 32px rgba(123,0,255,0.5)'
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.filter = 'brightness(1)'
                                e.currentTarget.style.boxShadow = '0 6px 24px rgba(123,0,255,0.35)'
                            }}
                        >
                            RÉSERVER MON INSTALLATION GRATUITE
                        </button>
                    </div>

                    <p
                        style={{
                            fontFamily: FONT,
                            fontSize: '15px',
                            fontWeight: 700,
                            color: '#111111',
                            textAlign: 'center',
                            marginTop: '14px',
                        }}
                    >
                        Agent actif en 48h · Smile Gen inclus pour six cabinets pilotes par ville
                    </p>
                </ScrollReveal>
            </main>

            <footer style={{ borderTop: '1px solid #e5e7eb', padding: '32px 24px', textAlign: 'center' }}>
                <p style={{ fontFamily: FONT, fontSize: '14px', color: '#9ca3af', margin: 0 }}>
                    © {new Date().getFullYear()} Cabinet Pilote · Forteuno. All rights reserved.
                </p>
            </footer>

            {shouldLoadModal && (
                <Suspense fallback={null}>
                    <LazyLeadCaptureModal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        landingSource={landingSource}
                        thankYouPath={thankYouPath}
                        unqualifiedThankYouPath={unqualifiedThankYouPath}
                    />
                </Suspense>
            )}
            </div>
        </div>
    )
}
